import {
  AccountCircle,
  ChevronRight,
  ExpandLess,
  Menu,
} from '@mui/icons-material';
import {
  Box,
  Collapse,
  Divider,
  SwipeableDrawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme,
} from '@mui/material';
import {
  Avatar,
  getRtlFlipStyle,
  isCapacitor,
  ZigButton,
  ZigSwitchLightDark,
  ZigTypography,
} from '@zignaly-open/ui';
import {
  ZigGlobeLanguagesIcon,
  ZigPlusIcon,
  ZigUserIcon,
} from '@zignaly-open/ui/icons';
import { useFirstOwnedService, useIsServiceOwner } from 'apis/service/use';
import {
  useChangeLocale,
  useCurrentUser,
  useIsAuthenticated,
  useLogout,
} from 'apis/user/use';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, Link, useLocation, useMatch } from 'react-router-dom';
import {
  ROUTE_2FA,
  ROUTE_BECOME_TRADER,
  ROUTE_EDIT_PROFILE,
  ROUTE_KYC,
  ROUTE_LOGIN,
  ROUTE_PASSWORD,
  ROUTE_MAIN_MARKETPLACE,
  ROUTE_REFERRALS,
  ROUTE_SIGNUP,
  ROUTE_SUBSCRIPTIONS,
  ROUTE_TRADING_SERVICE,
  ROUTE_TRADING_SERVICE_API,
  ROUTE_TRADING_SERVICE_EDIT,
  ROUTE_TRADING_SERVICE_INVESTORS,
  ROUTE_TRADING_SERVICE_MANAGE,
} from 'routes';
import { supportedLanguages } from 'util/i18n/i18next';
import { LocalizationLanguages } from 'util/i18n/languages';
import socialNetworksLinks from 'util/socialNetworks';
import { NavLink, Networks } from '../ExtraNavigationDropdown/styles';
import { DrawerMenuItem } from './atoms';
import { useOpenDepositModal } from '../../../views/Dashboard/components/ManageInvestmentModals/DepositModal';
import InviteIcon from '../../../images/tab-referrals.svg';
import {
  isAccountRequired,
  isFeatureOn,
  isZignaly,
  supportsNonCryptoServices,
  whitelabel,
} from '../../../whitelabel';
import { Features } from '../../../whitelabel/type';
import { openIntercom } from './util';
import { isInStandaloneMode } from '../../../sw/util';
import { gtagEvent } from '../../../util/analytics';
import { CAPACITOR_BUILD } from '../../../util/constants';
import { useDocumentDirection } from '../../../util/hooks/useUpdateDocumentRtl';

const drawerWidth = 250;

const ZigDrawer = () => {
  const location = useLocation();

  const [mobileOpen, setMobileOpen] = useState(false);

  const theme = useTheme();
  const handleDrawerToggle = () => {
    setMobileOpen((prevState) => !prevState);
    gtagEvent('hamburger_menu_click');
  };
  const logout = useLogout();
  const { t, i18n } = useTranslation('common');
  const isAuthenticated = useIsAuthenticated();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [managementOpen, setManagementOpen] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);
  const service = useFirstOwnedService();
  const { email, imageUrl } = useCurrentUser();
  const openDepositModal = useOpenDepositModal();
  const changeLocale = useChangeLocale();
  const match = useMatch(`${ROUTE_TRADING_SERVICE}/*`);
  const ownedServiceId = service?.serviceId?.toString();
  const serviceId = useIsServiceOwner(match?.params?.serviceId)
    ? match.params.serviceId
    : ownedServiceId;

  const languageMap = supportedLanguages
    ? supportedLanguages.map((x) => LocalizationLanguages[x])
    : Object.values(LocalizationLanguages);

  const handleSelectLanguage = (locale: string) => {
    changeLocale(locale);
    handleDrawerToggle();
  };

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  const dir = useDocumentDirection();

  return (
    <>
      <IconButton onClick={handleDrawerToggle} sx={{ mr: 2 }}>
        <Menu />
      </IconButton>
      <Box component='nav'>
        <SwipeableDrawer
          anchor={dir === 'rtl' ? 'right' : 'left'}
          onOpen={() => setMobileOpen(true)}
          variant='temporary'
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            '& .MuiPaper-root': {
              left: dir === 'rtl' ? 'unset /* @noflip */' : '0 /* @noflip */',
              right: dir === 'rtl' ? '0 /* @noflip */' : 'unset /* @noflip */',
            },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              paddingBottom: `env(safe-area-inset-bottom)`,
            },
          }}
        >
          <Box
            display='flex'
            flexDirection='column'
            textAlign='center'
            gap={2}
            sx={{ paddingTop: 'calc(8px + env(safe-area-inset-top))' }}
            flex={1}
          >
            {isAuthenticated ? (
              <Box
                sx={{ my: 2 }}
                display='flex'
                flexDirection='column'
                alignItems='center'
                gap={1.5}
              >
                {imageUrl ? (
                  <Avatar image={imageUrl} size={'x-large'} />
                ) : (
                  <AccountCircle sx={{ width: 55, height: 55 }} />
                )}
                <ZigTypography variant='caption'>{email}</ZigTypography>
              </Box>
            ) : (
              <Box
                mt={3}
                mb={2}
                display='flex'
                flexDirection='column'
                gap={2}
                onClick={handleDrawerToggle}
              >
                <Link to={ROUTE_LOGIN}>
                  <ZigButton
                    id={'drawer__login'}
                    variant='text'
                    startIcon={
                      <ZigUserIcon
                        color={theme.palette.neutral300}
                        width={'16px'}
                        height={'16px'}
                      />
                    }
                    color={'secondary'}
                  >
                    {t('account-menu.isAuth-button-logIn')}
                  </ZigButton>
                </Link>
                {!isFeatureOn(Features.LoginOnlyAccess) && (
                  <Link to={ROUTE_SIGNUP}>
                    <ZigButton id={'drawer__signup'} variant='contained'>
                      {t('account-menu.isAuth-button-signUp')}
                    </ZigButton>
                  </Link>
                )}
              </Box>
            )}
            <Divider />
            <List>
              {isAuthenticated ? (
                <>
                  <ListItemButton
                    onClick={() => setSettingsOpen(!settingsOpen)}
                  >
                    <ListItemText
                      primary={t('account-menu.notAuth-dropdown-link-settings')}
                    />
                    {settingsOpen ? (
                      <ExpandLess />
                    ) : (
                      <ChevronRight sx={getRtlFlipStyle()} />
                    )}
                  </ListItemButton>
                  <Collapse in={settingsOpen} timeout='auto' unmountOnExit>
                    <List component='div' disablePadding>
                      <DrawerMenuItem
                        id='drawer__profile'
                        path={generatePath(ROUTE_EDIT_PROFILE)}
                        closeDrawer={handleDrawerToggle}
                        label={t('account-menu.notAuth-dropdown-link-profile')}
                      />
                      <DrawerMenuItem
                        id='drawer__2fa'
                        path={generatePath(ROUTE_2FA)}
                        closeDrawer={handleDrawerToggle}
                        label={t('account-menu.notAuth-dropdown-link-2fa')}
                      />
                      <DrawerMenuItem
                        id='drawer__update-password'
                        path={generatePath(ROUTE_PASSWORD)}
                        closeDrawer={handleDrawerToggle}
                        label={t('account-menu.notAuth-dropdown-link-password')}
                      />
                      {isFeatureOn(Features.Kyc) && (
                        <DrawerMenuItem
                          id='drawer__kyc'
                          path={generatePath(ROUTE_KYC)}
                          closeDrawer={handleDrawerToggle}
                          label={t('account-menu.dropdown-link-kyc')}
                        />
                      )}
                    </List>
                  </Collapse>
                  {isFeatureOn(Features.CreateService) && (
                    <ListItem disablePadding onClick={handleDrawerToggle}>
                      <ListItemButton
                        id='drawer__become-trader'
                        to={ROUTE_BECOME_TRADER}
                        component={Link}
                      >
                        <ListItemText
                          primary={t('navigation-menu.become-trader')}
                        />
                      </ListItemButton>
                    </ListItem>
                  )}
                  {service && (
                    <>
                      <ListItemButton
                        id='drawer__for-trading'
                        onClick={() => setManagementOpen(!managementOpen)}
                      >
                        <ListItemText
                          primary={t('main-menu.dropdown-link-forTrading')}
                        />
                        {managementOpen ? (
                          <ExpandLess />
                        ) : (
                          <ChevronRight sx={getRtlFlipStyle()} />
                        )}
                      </ListItemButton>

                      <Collapse
                        in={managementOpen}
                        timeout='auto'
                        unmountOnExit
                      >
                        <List component='div' disablePadding>
                          <DrawerMenuItem
                            id='drawer__manage-funds'
                            path={generatePath(ROUTE_TRADING_SERVICE_MANAGE, {
                              serviceId: serviceId,
                            })}
                            closeDrawer={handleDrawerToggle}
                            label={t(
                              'account-menu.notAuth-dropdown-link-manage-funds',
                            )}
                          />
                          <DrawerMenuItem
                            id='drawer__manage-api-keys'
                            path={generatePath(ROUTE_TRADING_SERVICE_API, {
                              serviceId: serviceId,
                            })}
                            closeDrawer={handleDrawerToggle}
                            label={t(
                              'account-menu.notAuth-dropdown-link-manage-api',
                            )}
                          />
                          <DrawerMenuItem
                            id='drawer__manage-investors'
                            path={generatePath(
                              ROUTE_TRADING_SERVICE_INVESTORS,
                              {
                                serviceId: serviceId,
                              },
                            )}
                            closeDrawer={handleDrawerToggle}
                            label={t(
                              'account-menu.notAuth-dropdown-link-manage-investors',
                            )}
                          />
                          <DrawerMenuItem
                            id='drawer__manage-view-profile'
                            path={generatePath(ROUTE_TRADING_SERVICE, {
                              serviceId: serviceId,
                            })}
                            closeDrawer={handleDrawerToggle}
                            label={t(
                              'account-menu.notAuth-dropdown-link-manage-view-profile',
                            )}
                          />
                          <DrawerMenuItem
                            id='drawer__manage-edit-profile'
                            path={generatePath(ROUTE_TRADING_SERVICE_EDIT, {
                              serviceId: serviceId,
                            })}
                            closeDrawer={handleDrawerToggle}
                            label={t(
                              'account-menu.notAuth-dropdown-link-manage-edit-profile',
                            )}
                          />
                        </List>
                      </Collapse>
                    </>
                  )}
                  {isFeatureOn(Features.Subscriptions) && (
                    <ListItem disablePadding onClick={handleDrawerToggle}>
                      <ListItemButton
                        to={generatePath(ROUTE_SUBSCRIPTIONS)}
                        component={Link}
                        id={'drawer__subscriptions'}
                      >
                        <ListItemText
                          primary={t('account-menu.subscriptions')}
                        />
                      </ListItemButton>
                    </ListItem>
                  )}

                  {isFeatureOn(Features.Referrals) && (
                    <ListItem disablePadding onClick={handleDrawerToggle}>
                      <ListItemButton
                        to={generatePath(ROUTE_REFERRALS)}
                        component={Link}
                        id={'drawer__referrals'}
                      >
                        <ListItemText
                          primary={
                            <ZigTypography sx={{ display: 'flex' }}>
                              <InviteIcon
                                width={24}
                                height={24}
                                style={{ marginTop: -1, marginRight: 4 }}
                                color={theme.palette.neutral175}
                              />
                              {t('account-menu.referrals')}
                            </ZigTypography>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  )}
                </>
              ) : (
                (!isAccountRequired || isAuthenticated) && (
                  <ListItem disablePadding onClick={handleDrawerToggle}>
                    <ListItemButton
                      to={ROUTE_MAIN_MARKETPLACE}
                      component={Link}
                      id='drawer__ps'
                    >
                      <ListItemText
                        primary={t(
                          supportsNonCryptoServices
                            ? 'navigation-menu.marketplace-selector'
                            : 'navigation-menu.profit-sharing',
                        )}
                      />
                    </ListItemButton>
                  </ListItem>
                )
              )}
              {!!whitelabel.tools?.intercom && !isInStandaloneMode && (
                <ListItem disablePadding>
                  <ListItemButton
                    id='drawer__help-chat'
                    onClick={() => {
                      openIntercom();
                      handleDrawerToggle();
                    }}
                  >
                    <ListItemText primary={t('navigation-menu.ping-us')} />
                  </ListItemButton>
                </ListItem>
              )}

              {!!whitelabel.links?.helpUrl && (
                <ListItem disablePadding onClick={handleDrawerToggle}>
                  <ListItemButton
                    target='_blank'
                    href={whitelabel.links.helpUrl}
                  >
                    <ListItemText
                      primary={t('common:account-menu.help-center')}
                    />
                  </ListItemButton>
                </ListItem>
              )}

              {isAuthenticated && (
                <ListItem>
                  <ZigButton
                    id={'account-menu-deposit'}
                    startIcon={<ZigPlusIcon width={10} height={10} />}
                    sx={{ fontWeight: 600, mb: 1 }}
                    variant={'contained'}
                    onClick={() => openDepositModal()}
                  >
                    {t('action:deposit')}
                  </ZigButton>
                </ListItem>
              )}
              {isZignaly && (
                <ListItem>
                  <ZigSwitchLightDark />
                </ListItem>
              )}
            </List>
            <List>
              <ListItem disablePadding>
                <ListItemButton onClick={() => setLanguageOpen(!languageOpen)}>
                  <ListItemIcon sx={{ minWidth: '48px' }}>
                    <ZigGlobeLanguagesIcon
                      color={theme.palette.neutral300}
                      width={'26px'}
                      height={'26px'}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      LocalizationLanguages[i18n.language?.split('_')[0]]?.label
                    }
                  />

                  {languageOpen ? (
                    <ExpandLess />
                  ) : (
                    <ChevronRight sx={getRtlFlipStyle()} />
                  )}
                </ListItemButton>
              </ListItem>
              <Collapse in={languageOpen} timeout='auto' unmountOnExit>
                <List component='div' disablePadding>
                  {languageMap.map((language, index) => (
                    <ListItemButton
                      sx={{
                        pl: 4,
                      }}
                      key={language.locale}
                      onClick={() => handleSelectLanguage(language.locale)}
                      id={`drawer-languages__${index.toString()}`}
                      selected={i18n.language === language.locale}
                    >
                      <ListItemText primary={language.label} />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            </List>
            {isAuthenticated && (
              <ZigButton
                onClick={() => {
                  logout();
                  handleDrawerToggle();
                }}
                variant='outlined'
                sx={{ mx: 4, mY: 1 }}
              >
                {t('account-menu.notAuth-button-logOut')}
              </ZigButton>
            )}

            {socialNetworksLinks.length > 0 && (
              <>
                <ZigTypography mt={2} variant='caption' marginTop='auto'>
                  {t('follow-us')}
                </ZigTypography>
                <Box display='flex' justifyContent='center' mt={-1} mb={1}>
                  <Networks>
                    {socialNetworksLinks.map((socialNetwork, index) => {
                      const IconComponent = socialNetwork.image;
                      return (
                        <NavLink
                          onClick={handleDrawerToggle}
                          href={socialNetwork.path}
                          key={`--social-network-nav-link-${index.toString()}`}
                          id={`drawer__social-network-${index.toString()}`}
                          target={'_blank'}
                        >
                          <IconComponent height={'22px'} width={'22px'} />
                        </NavLink>
                      );
                    })}
                  </Networks>
                </Box>
              </>
            )}

            {isCapacitor && CAPACITOR_BUILD && (
              <ZigTypography
                sx={{ fontSize: '10px', mt: -2, mb: 1, opacity: 0.6 }}
              >
                {t('common:build-hash', { hash: CAPACITOR_BUILD })}
              </ZigTypography>
            )}
          </Box>
        </SwipeableDrawer>
      </Box>
    </>
  );
};

export default ZigDrawer;
